import React from "react";
import { Box, Chip, IconButton, Typography, Tooltip, Avatar } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import FolderIcon from '@mui/icons-material/Folder';
import LockOpenIcon from "@mui/icons-material/LockOpen";
import CircularIndeterminate from "../Loader.component";
import StatusChip from "../common/StatusChip";
import { Icon as OpusIcon } from "../common/opus";
import moment from "moment"; // Import moment
import getAvatarColor, { getInitials } from "../../utils/get-initial-logo";
import { AE_palette } from "../../style/theme";
import CaptionLayerSummary from "./CaptionLayerSummary";
import { color } from "@mui/system";

const SceneList = ({
  scenes,
  loading,
  editMode,
  onEditScene,
  onEditOld,
  onManageAssets,
  selectedScenes,
  onSceneSelect,
  onDeleteScene,
  onLockScene,
  onUnlockScene,
  canAccessLock = false,
  onAccessLock,
}) => {
  const columns = [
    {
      field: "name",
      headerName: "Scene Name",
      flex: 1,
      renderCell: (params) => (
        <Typography
          onClick={() => onEditScene(params.row.id)}
          sx={{ fontSize: '13.5px', fontWeight: 650, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "created_at",
      headerName: "Created",
      flex: 1,
      renderCell: (params) => {
        return params.value ? (
          <Typography variant="body2">
            {moment(params.value).format('MMM D, YYYY h:mm A')}
          </Typography>
        ) : "-";
      }
    },
    {
      field: "status",
      headerName: "Status",
      flex: .5,
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
            <StatusChip status={params.value} />
            {params.row.locked && params.row.locked.lockedStatus && (
              <Tooltip
                title={
                  params.row.locked.lockedByUser
                    ? `Locked by ${params.row.locked.lockedByUser.name} (${params.row.locked.lockedByUser.email})`
                    : "Locked"
                }
              >
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    ml: 1,
                    fontSize: '12px',
                    bgcolor: getAvatarColor(params.row.locked.lockedByUser?.name || ''),
                    border: AE_palette.seafoamGreen,
                    color: AE_palette.white
                  }}
                >
                  {getInitials(params.row.locked.lockedByUser?.name || '')}
                </Avatar>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      renderCell: (params) => {
        return params.value ? (
          <Typography variant="body2">
          {params.row.created_by_user?.name || params.value.created_by_user?.name || "Unknown User"}
          </Typography>
        ) : "-";
      }
    },
    {
      // OPUS-1391: a per-layer breakdown rather than the first two caption
      // names. Names never fit — a scene with 20 captions rendered as two pills
      // and a "+18" that said nothing — whereas "how much sits on each layer"
      // is both a fixed width and the thing worth scanning a column of scenes
      // for. All three layers always render, in stacking order, so counts line
      // up down the column; empty ones dim rather than disappear.
      field: "captionNames",
      headerName: "Layers",
      flex: 3,
      sortable: false,
      renderCell: (params) => <CaptionLayerSummary captions={params?.row?.captions} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 160,
      type: 'number',
      renderCell: (params) => {
        const isLocked = params.row.locked && params.row.locked.lockedStatus;
        return (
         
          <>
            {!isLocked && (
              <>
               {/* <IconButton
                onClick={() => onEditOld(params.row)}
                color="info"
              >
                <OpusIcon name="edit" size={18} />
              </IconButton> */}
              <Tooltip title="Manage Assets">
                <IconButton
                  onClick={() => onManageAssets(params.row.id)}
                  color="info"
                >
                  <OpusIcon name="gallery" size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => onEditScene(params.row.id)}
                  color="info"
                >
                  <OpusIcon name="edit" size={18} />
                </IconButton>
              </Tooltip>
              </>
            )}
            {isLocked ? (
              <Tooltip title="Unlock Scene">
                <IconButton
                  onClick={() => onUnlockScene(params.row)}
                  color="warning"
                  size="small"
                >
                  <OpusIcon name="unlock" size={18} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Lock Scene">
                <IconButton
                  onClick={() => onLockScene(params.row)}
                  color="default"
                  size="small"
                >
                  <OpusIcon name="lock" size={18} />
                </IconButton>
              </Tooltip>
            )}

            {editMode && !isLocked && (
              <Tooltip title="Delete">
                <IconButton
                  onClick={() => onDeleteScene(params.row)}
                  color="error"
                >
                  <OpusIcon name="trash" size={18} />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  return (
    <DataGrid
      rows={scenes}
      columns={columns}
      getRowId={(row) => row?.id || row?._id}
      checkboxSelection={editMode}
      disableRowSelectionOnClick
      loading={loading}
      rowHeight={52}
      columnHeaderHeight={44}
      selectionModel={selectedScenes}
      onSelectionModelChange={(newSelection) => {
        onSceneSelect(newSelection);
      }}
      sx={{
        height: '100%',
        border: 'none',
        // OPUS-913: header/row dividers + cell padding now come from the global
        // MuiDataGrid theme (matches the design DataTable); no per-grid overrides.
        '& .MuiDataGrid-footerContainer': {
          borderTop: 'none',
        },
      }}
      getRowClassName={(params) => {
        return (params.row.locked && params.row.locked.lockedStatus) ? 'row-locked' : '';
      }}
    />
  );
};

export default SceneList;